/**
 * Image Service - AI-Powered Intelligent Image Selection
 *
 * OPTIMIZED AI FLOW (Single AI Call):
 * 1. getImageStrategy() - ONE AI call that:
 *    - Analyzes activity details (category, title, description, location)
 *    - Decides best source: TMDB (movies), Google Places (venues), or Unsplash (generic)
 *    - Generates optimized search query
 *    - Returns: { source, confidence, query, reasoning }
 *
 * 2. Execute based on AI decision:
 *    - TMDB: searchMoviePosterDirect(query) - clean movie title
 *    - Google Places: searchVenuePhotoDirect(query) - venue name + location
 *    - Unsplash: searchUnsplashImages(query) - descriptive keywords
 *
 * 3. Fallback chain:
 *    - Recommended source fails → Try Unsplash with AI query
 *    - Unsplash fails → Use curated images
 *    - All fail → Default fallback image
 *
 * KEY FEATURES:
 * - Single AI call per activity (cached)
 * - Context-aware: Venues trump activity type ("games at Mind's Cafe" → venue photo)
 * - Request deduplication (5s TTL)
 * - Smart caching: Strategy, TMDB, Google Places, keywords
 *
 * DEPRECATED FUNCTIONS (kept for compatibility):
 * - generateAIKeywords() - replaced by getImageStrategy()
 * - extractMovieTitleWithAI() - replaced by getImageStrategy()
 * - generateSearchTerms() - replaced by getImageStrategy()
 * - searchMoviePoster() - replaced by searchMoviePosterDirect()
 * - searchVenuePhoto() - replaced by searchVenuePhotoDirect()
 */

import { ActivityCategory } from "../types";
// @ts-ignore
import fallBackImage from "../src/assets/1024.png";
import { GoogleGenAI } from "@google/genai";
import { crashlytics, getApiKeys } from "./firebase";

// Simple cache to prevent duplicate image fetches
const imageCache = new Map<string, Promise<string>>();

// Cache for AI-generated keywords
const keywordCache = new Map<string, string[]>();

// Cache for TMDB movie posters
const moviePosterCache = new Map<string, string>();

// Track call count for debugging
let callCount = 0;

const TMDB_API_URL = "https://api.themoviedb.org/3";
const TMDB_IMAGE_BASE_URL = "https://image.tmdb.org/t/p/w500";

// Google Places API configuration
const GOOGLE_PLACES_PHOTO_BASE_URL = "https://places.googleapis.com/v1";

// Cache for venue photos
const venuePosterCache = new Map<string, string>();

// Cache for AI image strategy decisions
const imageStrategyCache = new Map<string, ImageStrategy>();

// Helper to get Gemini client
const getAIClient = async (): Promise<GoogleGenAI> => {
  const { GOOGLE_API_KEY } = await getApiKeys();
  if (!GOOGLE_API_KEY) {
    throw new Error("API Key not found for AI keyword generation.");
  }
  return new GoogleGenAI({ apiKey: GOOGLE_API_KEY });
};

// Type for AI image strategy decision
interface ImageStrategy {
  source: "tmdb" | "google_places" | "unsplash";
  confidence: number;
  query: string;
  reasoning: string;
}

// Prompt template cache
const promptCache: Record<string, string> = {};

// Helper to load prompt templates from files
async function loadPrompt(name: string): Promise<string> {
  if (promptCache[name]) return promptCache[name];
  try {
    const response = await fetch(`/prompts/${name}.txt`);
    const text = await response.text();
    promptCache[name] = text;
    return text;
  } catch (error) {
    crashlytics.logError(
      `Error loading prompt ${name}: ${JSON.stringify(error?.message)}`,
      "imageService.ts",
      error,
    );
    return ""; // Fallback to inline prompts if file loading fails
  }
}

// Helper function to normalize category strings to valid ActivityCategory enum values
function normalizeCategory(
  category: string | ActivityCategory,
): ActivityCategory {
  // If it's already a valid enum value, return it
  if (Object.values(ActivityCategory).includes(category as ActivityCategory)) {
    return category as ActivityCategory;
  }

  // Normalize string categories to enum values
  const normalizedCategory = category.toLowerCase().replace(/[^a-z]/g, "");

  // Map common variations to existing categories - properly distributed
  const categoryMapping: Record<string, ActivityCategory> = {
    // Movies & Entertainment
    movie: ActivityCategory.Movies,
    film: ActivityCategory.Movies,
    cinema: ActivityCategory.Movies,
    theater: ActivityCategory.Movies,
    theatre: ActivityCategory.Movies,
    screening: ActivityCategory.Movies,
    premiere: ActivityCategory.Movies,
    show: ActivityCategory.Movies,
    series: ActivityCategory.Movies,
    documentary: ActivityCategory.Movies,

    // Cultural & Arts (General, not Games!)
    culturalexperience: ActivityCategory.General,
    cultural: ActivityCategory.General,
    culture: ActivityCategory.General,
    museum: ActivityCategory.General,
    gallery: ActivityCategory.General,
    artgallery: ActivityCategory.General,
    exhibition: ActivityCategory.General,
    art: ActivityCategory.General,
    performance: ActivityCategory.General,
    concert: ActivityCategory.General,
    music: ActivityCategory.General,

    // Gaming & Games (only actual games)
    gaming: ActivityCategory.Games,
    game: ActivityCategory.Games,
    videogames: ActivityCategory.Games,
    esports: ActivityCategory.Games,
    boardgames: ActivityCategory.Games,
    cardgames: ActivityCategory.Games,
    arcade: ActivityCategory.Games,
    quiz: ActivityCategory.Games,
    trivia: ActivityCategory.Games,
    karaoke: ActivityCategory.Games,
    bowling: ActivityCategory.Games,

    // Food & Dining
    fooddrinks: ActivityCategory.Dining,
    foodanddrinks: ActivityCategory.Dining,
    food: ActivityCategory.Dining,
    dining: ActivityCategory.Dining,
    restaurant: ActivityCategory.Dining,
    meal: ActivityCategory.Dining,
    lunch: ActivityCategory.Dining,
    dinner: ActivityCategory.Dining,
    breakfast: ActivityCategory.Dining,
    brunch: ActivityCategory.Dining,

    // Drinks & Coffee
    coffeeandchat: ActivityCategory.Drinks,
    coffee: ActivityCategory.Drinks,
    drinks: ActivityCategory.Drinks,
    bar: ActivityCategory.Drinks,
    cocktail: ActivityCategory.Drinks,
    cocktails: ActivityCategory.Drinks,
    wine: ActivityCategory.Drinks,
    beer: ActivityCategory.Drinks,
    brewery: ActivityCategory.Drinks,
    cafe: ActivityCategory.Drinks,

    // Fitness & Sports
    fitness: ActivityCategory.Fitness,
    gym: ActivityCategory.Fitness,
    workout: ActivityCategory.Fitness,
    exercise: ActivityCategory.Fitness,
    yoga: ActivityCategory.Fitness,
    pilates: ActivityCategory.Fitness,
    dance: ActivityCategory.Fitness,
    sports: ActivityCategory.Fitness,
    training: ActivityCategory.Fitness,

    // Outdoor Activities
    outdoor: ActivityCategory.Outdoor,
    hiking: ActivityCategory.Outdoor,
    nature: ActivityCategory.Outdoor,
    park: ActivityCategory.Outdoor,
    beach: ActivityCategory.Outdoor,
    camping: ActivityCategory.Outdoor,
    travel: ActivityCategory.Outdoor,
    adventure: ActivityCategory.Outdoor,
    walking: ActivityCategory.Outdoor,
    cycling: ActivityCategory.Outdoor,
    running: ActivityCategory.Outdoor,

    // General & Social
    social: ActivityCategory.General,
    meetup: ActivityCategory.General,
    events: ActivityCategory.General,
    shopping: ActivityCategory.General,
    tech: ActivityCategory.General,
    books: ActivityCategory.General,
    general: ActivityCategory.General,
    community: ActivityCategory.General,
    networking: ActivityCategory.General,
    workshop: ActivityCategory.General,
    seminar: ActivityCategory.General,
  };

  return categoryMapping[normalizedCategory] || ActivityCategory.General; // Default fallback
}

/**
 * AI-powered image strategy selector
 * Uses Gemini to intelligently choose the best image source and search query
 */
async function getImageStrategy(
  category: ActivityCategory,
  title?: string,
  description?: string,
  location?: string,
): Promise<ImageStrategy> {
  // Create cache key
  const cacheKey = `${category}-${title || ""}-${description || ""}-${location || ""}`;

  // Check cache first
  if (imageStrategyCache.has(cacheKey)) {
    const cached = imageStrategyCache.get(cacheKey)!;
    return cached;
  }

  try {
    const client = await getAIClient();

    // Load prompt template
    const promptTemplate = await loadPrompt("image-strategy");
    const prompt = promptTemplate
      .replace("{{CATEGORY}}", category)
      .replace("{{TITLE}}", title || "Not specified")
      .replace("{{DESCRIPTION}}", description || "Not specified")
      .replace("{{LOCATION}}", location || "Not specified");

    const stream = await client.models.generateContentStream({
      model: "gemini-2.5-flash-lite",
      contents: [{ parts: [{ text: prompt }] }],
      config: {
        responseMimeType: "application/json",
        temperature: 0.3, // Low temperature for consistent decisions
      },
    });

    let fullText = "";
    for await (const chunk of stream) {
      if (chunk.text) {
        fullText += chunk.text;
      }
    }

    // Clean up the response - remove any markdown code blocks
    let jsonText = fullText.trim();
    if (jsonText.startsWith("```json")) {
      jsonText = jsonText.replace(/^```json\s*/, "").replace(/\s*```$/, "");
    } else if (jsonText.startsWith("```")) {
      jsonText = jsonText.replace(/^```\s*/, "").replace(/\s*```$/, "");
    }

    // Try to parse JSON response
    let strategy: ImageStrategy;
    try {
      strategy = JSON.parse(jsonText);
    } catch (parseError) {
      crashlytics.logError(
        `Error parsing JSON response: ${JSON.stringify(parseError?.message)}`,
        "imageService.ts",
        parseError,
      );
      throw new Error(`Invalid JSON response: ${jsonText.substring(0, 100)}`);
    }

    // Validate strategy object
    if (
      !strategy.source ||
      !strategy.query ||
      typeof strategy.confidence !== "number"
    ) {
      crashlytics.logError(
        `Strategy missing required fields: ${JSON.stringify(strategy)}`,
        "imageService.ts",
        new Error("Strategy missing required fields"),
      );
      throw new Error("Strategy missing required fields");
    }


    // Cache the result
    imageStrategyCache.set(cacheKey, strategy);
    return strategy;
  } catch (error) {
    console.warn("AI strategy selection failed, using fallback logic:", error);

    // Enhanced fallback logic with better venue detection
    const text = `${title || ""} ${description || ""}`.toLowerCase();

    // Check for venue indicators (at, in, specific business-like names)
    const venuePattern = /(?:at|@|in)\s+([A-Z][A-Za-z\s&.''-]+)/i;
    const match =
      (title || "").match(venuePattern) ||
      (description || "").match(venuePattern);

    if (
      match &&
      (category === ActivityCategory.Dining ||
        category === ActivityCategory.Drinks ||
        category === ActivityCategory.Games ||
        text.includes("cafe") ||
        text.includes("restaurant") ||
        text.includes("bar"))
    ) {
      const venueName = match[1].trim();
      return {
        source: "google_places",
        confidence: 65,
        query: `${venueName} ${location || "Singapore"}`,
        reasoning: `Fallback: Venue detected - "${venueName}"`,
      };
    }

    // Fallback for movies
    if (category === ActivityCategory.Movies) {
      return {
        source: "tmdb",
        confidence: 70,
        query: title || "movie",
        reasoning: "Fallback: Movies category detected",
      };
    }

    // Default to Unsplash for generic activities
    return {
      source: "unsplash",
      confidence: 50,
      query: title || category,
      reasoning: "Fallback: Default to Unsplash",
    };
  }
}

/**
 * Generate AI-powered search keywords for image search
 * Uses Gemini to analyze activity context and suggest optimal keywords
 *
 * @deprecated This function is no longer used in the main flow.
 * Use getImageStrategy() instead which provides optimized queries directly.
 * Kept for backward compatibility.
 */
async function generateAIKeywords(
  category: ActivityCategory,
  title?: string,
  description?: string,
): Promise<string[]> {
  // Create cache key
  const cacheKey = `${category}-${title || ""}-${description || ""}`;

  // Check cache first
  if (keywordCache.has(cacheKey)) {
    const cachedKeywords = keywordCache.get(cacheKey)!;
    return cachedKeywords;
  }

  try {
    const client = await getAIClient();

    // Load prompt template
    const promptTemplate = await loadPrompt("image-keyword-generation");
    const prompt = promptTemplate
      .replace("{{CATEGORY}}", category)
      .replace("{{TITLE}}", title || "Not specified")
      .replace("{{DESCRIPTION}}", description || "Not specified");

    const stream = await client.models.generateContentStream({
      model: "gemini-2.5-flash-lite",
      contents: [{ parts: [{ text: prompt }] }],
      config: {
        responseMimeType: "application/json",
        temperature: 0.7,
      },
    });

    let fullText = "";
    for await (const chunk of stream) {
      if (chunk.text) {
        fullText += chunk.text;
      }
    }

    // Parse JSON response
    const keywords = JSON.parse(fullText.trim());

    if (Array.isArray(keywords) && keywords.length > 0) {
      // Cache the results
      keywordCache.set(cacheKey, keywords);
      return keywords;
    }

    // Fallback if parsing fails
    return [];
  } catch (error) {
    console.warn("AI keyword generation failed:", error);
    return [];
  }
}

/**
 * Extract movie title using AI
 * Uses Gemini to intelligently parse activity details and extract the actual movie title
 *
 * @deprecated This function is replaced by getImageStrategy() which extracts movie titles directly.
 * Only used by the legacy searchMoviePoster() function.
 * Kept for backward compatibility.
 */
async function extractMovieTitleWithAI(
  title?: string,
  description?: string,
): Promise<string | null> {
  if (!title && !description) return null;

  // Create cache key
  const cacheKey = `movie-title-${title}-${description}`;

  try {
    const client = await getAIClient();

    // Load prompt template
    const promptTemplate = await loadPrompt("movie-title-extraction");
    const prompt = promptTemplate
      .replace("{{TITLE}}", title || "Not specified")
      .replace("{{DESCRIPTION}}", description || "Not specified");

    const stream = await client.models.generateContentStream({
      model: "gemini-2.5-flash-lite",
      contents: [{ parts: [{ text: prompt }] }],
      config: {
        temperature: 0.3, // Lower temperature for more consistent extraction
      },
    });

    let fullText = "";
    for await (const chunk of stream) {
      if (chunk.text) {
        fullText += chunk.text;
      }
    }

    const extractedTitle = fullText.trim();

    if (extractedTitle && extractedTitle.length > 1) {
      return extractedTitle;
    }

    return null;
  } catch (error) {
    console.warn("AI movie title extraction failed:", error);
    return null;
  }
}

/**
 * Direct TMDB search using a clean query
 * Used when AI has already extracted the movie title
 */
async function searchMoviePosterDirect(query: string): Promise<string | null> {
  const { TMDB_API_KEY } = await getApiKeys();
  if (!TMDB_API_KEY) {
    console.warn("TMDB API key not found.");
    return null;
  }
  try {
    const cacheKey = `movie-${query.toLowerCase()}`;
    if (moviePosterCache.has(cacheKey)) {
      const cachedPoster = moviePosterCache.get(cacheKey)!;
      return cachedPoster;
    }
    const searchUrl = `${TMDB_API_URL}/search/movie?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(query)}`;
    const response = await fetch(searchUrl);
    if (!response.ok) {
      console.warn("TMDB API error:", response.status);
      return null;
    }
    const data = await response.json();
    if (data.results && data.results.length > 0) {
      const movie = data.results[0];
      if (movie.poster_path) {
        const posterUrl = `${TMDB_IMAGE_BASE_URL}${movie.poster_path}`;
        moviePosterCache.set(cacheKey, posterUrl);
        return posterUrl;
      }
    }
    return null;
  } catch (error) {
    console.warn("Error fetching from TMDB:", error);
    return null;
  }
}

/**
 * Direct Google Places search using a clean query
 * Used when AI has already extracted venue name + location
 */
async function searchVenuePhotoDirect(query: string): Promise<string | null> {
  const { GOOGLE_API_KEY } = await getApiKeys();
  if (!GOOGLE_API_KEY) {
    console.warn("Google Places API key not found.");
    return null;
  }

  try {
    const cacheKey = `venue-${query.toLowerCase()}`;
    if (venuePosterCache.has(cacheKey)) {
      const cachedPhoto = venuePosterCache.get(cacheKey)!;
      return cachedPhoto;
    }


    const searchUrl = `https://places.googleapis.com/v1/places:searchText`;
    const searchResponse = await fetch(searchUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": GOOGLE_API_KEY,
        "X-Goog-FieldMask": "places.photos,places.displayName",
      },
      body: JSON.stringify({
        textQuery: query,
        maxResultCount: 1,
      }),
    });

    if (!searchResponse.ok) {
      console.warn("Google Places API error:", searchResponse.status);
      return null;
    }

    const searchData = await searchResponse.json();

    if (searchData.places && searchData.places.length > 0) {
      const place = searchData.places[0];

      if (place.photos && place.photos.length > 0) {
        const photo = place.photos[0];
        const photoUrl = `https://places.googleapis.com/v1/${photo.name}/media?key=${GOOGLE_API_KEY}&maxHeightPx=600&maxWidthPx=400`;

        venuePosterCache.set(cacheKey, photoUrl);
        return photoUrl;
      }
    }

    return null;
  } catch (error) {
    console.warn("Error fetching from Google Places:", error);
    return null;
  }
}

/**
 * Search for movie poster using TMDB API
 * Extracts movie title from activity details and fetches poster
 *
 * @deprecated This function is replaced by searchMoviePosterDirect() which is used with getImageStrategy().
 * Kept for backward compatibility.
 */
async function searchMoviePoster(
  title?: string,
  description?: string,
): Promise<string | null> {
  const { TMDB_API_KEY } = await getApiKeys();
  if (!TMDB_API_KEY) {
    console.warn(
      "TMDB API key not found. Set TMDB_API_KEY or VITE_TMDB_API_KEY environment variable.",
    );
    return null;
  }

  try {
    // Extract potential movie title from activity title or description
    const text = `${title || ""} ${description || ""}`.toLowerCase();

    // Common movie-related keywords to help identify movie titles
    const movieKeywords = [
      "watching",
      "watch",
      "movie",
      "film",
      "cinema",
      "screening",
    ];
    const hasMovieContext = movieKeywords.some((keyword) =>
      text.includes(keyword),
    );

    if (!hasMovieContext) {
      return null; // Not a movie-related activity
    }


    // Try AI-powered extraction first (more accurate)
    let movieTitle = await extractMovieTitleWithAI(title, description);

    // Fallback to regex-based extraction if AI fails
    if (!movieTitle) {
      movieTitle = title || "";

      // Remove common prefixes and suffixes
      movieTitle = movieTitle
        .replace(/^(watching|watch|movie night|film|cinema|screening)\s*/gi, "")
        .replace(
          /\s*(movie|film|night|at cinema|screening|tonight|today)$/gi,
          "",
        )
        .trim();
    }

    if (!movieTitle || movieTitle.length < 2) {
      return null; // No clear movie title
    }

    // Check cache first
    const cacheKey = movieTitle.toLowerCase();
    if (moviePosterCache.has(cacheKey)) {
      const cachedPoster = moviePosterCache.get(cacheKey)!;
      return cachedPoster;
    }


    // Search TMDB for the movie
    const searchUrl = new URL(`${TMDB_API_URL}/search/movie`);
    searchUrl.searchParams.set("api_key", TMDB_API_KEY);
    searchUrl.searchParams.set("query", movieTitle);
    searchUrl.searchParams.set("include_adult", "false");

    const response = await fetch(searchUrl.toString());

    if (!response.ok) {
      console.warn("TMDB API error:", response.status);
      return null;
    }

    const data = await response.json();

    if (data.results && data.results.length > 0) {
      const movie = data.results[0]; // Take the first/most relevant result
      if (movie.poster_path) {
        const posterUrl = `${TMDB_IMAGE_BASE_URL}${movie.poster_path}`;
        moviePosterCache.set(cacheKey, posterUrl);
        return posterUrl;
      }
    }

    return null;
  } catch (error) {
    console.warn("Error fetching movie poster from TMDB:", error);
    return null;
  }
}

/**
 * Search for venue photo using Google Places API
 * Extracts venue name and location from activity details and fetches photos
 *
 * @deprecated This function is replaced by searchVenuePhotoDirect() which is used with getImageStrategy().
 * Kept for backward compatibility.
 */
async function searchVenuePhoto(
  title?: string,
  description?: string,
  location?: string,
): Promise<string | null> {
  const { GOOGLE_API_KEY } = await getApiKeys();
  if (!GOOGLE_API_KEY) {
    console.warn("Google Places API key not found.");
    return null;
  }

  try {
    // Combine title and description to extract venue name
    const text = `${title || ""} ${description || ""}`;

    // Common venue indicators (at, @, in, near)
    const venuePattern =
      /(?:at|@|in|near)\s+([A-Z][A-Za-z\s&.''-]+?)(?:\s+(?:restaurant|cafe|bar|pub|restaurant|bistro|eatery|diner|brewery|rooftop|lounge|club))?(?:\s*[,.]|\s+in\s+|$)/i;
    const match = text.match(venuePattern);

    let venueName = match ? match[1].trim() : null;

    // If no "at/in" pattern, check if title itself is a venue name (capitalized words)
    if (!venueName && title) {
      // Check if title looks like a venue name (has capital letters, not generic)
      const genericWords = [
        "brunch",
        "dinner",
        "lunch",
        "breakfast",
        "coffee",
        "drinks",
        "eating",
        "dining",
      ];
      const titleLower = title.toLowerCase();
      const isGeneric = genericWords.some((word) => titleLower.includes(word));

      if (!isGeneric && /^[A-Z]/.test(title)) {
        venueName = title
          .replace(
            /\s+(brunch|dinner|lunch|breakfast|night|time|session)$/i,
            "",
          )
          .trim();
      }
    }

    if (!venueName || venueName.length < 3) {
      return null; // No clear venue name
    }


    // Check cache first
    const cacheKey = `${venueName.toLowerCase()}-${location || ""}`;
    if (venuePosterCache.has(cacheKey)) {
      const cachedPhoto = venuePosterCache.get(cacheKey)!;
      return cachedPhoto;
    }

    // Build search query
    const searchQuery = location
      ? `${venueName} ${location}`
      : `${venueName} Singapore`; // Default to Singapore


    // Search for the place using Text Search (New)
    const searchUrl = `https://places.googleapis.com/v1/places:searchText`;

    const searchResponse = await fetch(searchUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": GOOGLE_API_KEY,
        "X-Goog-FieldMask": "places.photos,places.displayName",
      },
      body: JSON.stringify({
        textQuery: searchQuery,
        maxResultCount: 1,
      }),
    });

    if (!searchResponse.ok) {
      console.warn("Google Places API error:", searchResponse.status);
      return null;
    }

    const searchData = await searchResponse.json();

    if (searchData.places && searchData.places.length > 0) {
      const place = searchData.places[0];

      if (place.photos && place.photos.length > 0) {
        // Get the first photo
        const photo = place.photos[0];
        const photoName = photo.name;

        // Construct photo URL using the new API
        const photoUrl = `https://places.googleapis.com/v1/${photoName}/media?key=${GOOGLE_API_KEY}&maxHeightPx=600&maxWidthPx=400`;

        venuePosterCache.set(cacheKey, photoUrl);
        return photoUrl;
      }
    }

    return null;
  } catch (error) {
    console.warn("Error fetching venue photo from Google Places:", error);
    return null;
  }
}

// Using Unsplash API for high-quality, relevant images
const UNSPLASH_API_URL = "https://api.unsplash.com/search/photos";

// Category to search keywords mapping - prioritizing relevant subjects with bright, lively settings
const CATEGORY_KEYWORDS: Record<ActivityCategory, string[]> = {
  [ActivityCategory.Movies]: [
    "movie theater cinema seats",
    "popcorn cinema snacks",
    "film premiere event",
    "home movie night",
    "cinema entrance marquee",
  ],
  [ActivityCategory.Dining]: [
    "restaurant food dining",
    "gourmet food plating",
    "dinner table setting",
    "chef cooking kitchen",
    "fine dining restaurant",
  ],
  [ActivityCategory.Games]: [
    "board games friends playing",
    "video gaming setup",
    "arcade games",
    "card games table",
    "game night friends",
  ],
  [ActivityCategory.Outdoor]: [
    "hiking trail mountains",
    "park picnic outdoors",
    "beach volleyball sand",
    "camping mountains",
    "outdoor activities nature",
  ],
  [ActivityCategory.Drinks]: [
    "coffee shop cafe",
    "cocktails bar drinks",
    "wine tasting glasses",
    "craft beer brewery",
    "tea ceremony",
  ],
  [ActivityCategory.Fitness]: [
    "gym fitness workout",
    "yoga class exercise",
    "running outdoors fitness",
    "dance class studio",
    "fitness training gym",
  ],
  [ActivityCategory.General]: [
    "friends gathering social",
    "community event meeting",
    "networking professional",
    "casual hangout friends",
    "group activity social",
  ],
};

// Fallback images for each category using bright, vibrant, colorful Unsplash images
const FALLBACK_IMAGES: Record<ActivityCategory, string> = {
  [ActivityCategory.Movies]:
    "https://images.unsplash.com/photo-1489570403014-7e84b51d5ba3?w=400&h=600&fit=crop&auto=format&sat=20&con=10", // Cinema with bright red seats
  [ActivityCategory.Dining]:
    "https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=400&h=600&fit=crop&auto=format&sat=20&con=10", // Colorful gourmet food
  [ActivityCategory.Games]:
    "https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=400&h=600&fit=crop&auto=format&sat=20&con=10", // Bright colorful board games
  [ActivityCategory.Outdoor]:
    "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=600&fit=crop&auto=format&sat=20&con=10", // Bright sunny hiking
  [ActivityCategory.Drinks]:
    "https://images.unsplash.com/photo-1551218808-94e220e084d2?w=400&h=600&fit=crop&auto=format&sat=20&con=10", // Vibrant colorful cocktails
  [ActivityCategory.Fitness]:
    "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=400&h=600&fit=crop&auto=format&sat=20&con=10", // Bright outdoor fitness
  [ActivityCategory.General]:
    "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=400&h=600&fit=crop&auto=format&sat=20&con=10", // Colorful community gathering
};

// Additional curated bright and vibrant Unsplash images for variety
const CURATED_IMAGES: Record<ActivityCategory, string[]> = {
  [ActivityCategory.Movies]: [
    "https://images.unsplash.com/photo-1489570403014-7e84b51d5ba3?w=400&h=600&fit=crop&sat=20&con=10", // Cinema with red seats
    "https://images.unsplash.com/photo-1598899134739-24c46f58b8c0?w=400&h=600&fit=crop&sat=20&con=10", // Movie theater with popcorn
    "https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=400&h=600&fit=crop&sat=25&con=10", // Colorful popcorn
    "https://images.unsplash.com/photo-1594909122845-11baa439b7bf?w=400&h=600&fit=crop&sat=20&con=10", // Home theater setup
    "https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=400&h=600&fit=crop&sat=20&con=10", // Cinema marquee lights
  ],
  [ActivityCategory.Dining]: [
    "https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=400&h=600&fit=crop&sat=25&con=15", // Vibrant colorful gourmet food
    "https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=400&h=600&fit=crop&sat=20&con=10", // Bright colorful pancakes
    "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=600&fit=crop&sat=20&con=15", // Colorful chef cooking
    "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400&h=600&fit=crop&sat=15&con=10", // Warm restaurant lighting
    "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&h=600&fit=crop&sat=20&con=10", // Bright restaurant interior
  ],
  [ActivityCategory.Games]: [
    "https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=400&h=600&fit=crop&sat=25&con=15", // Bright colorful board games
    "https://images.unsplash.com/photo-1511512578047-dfb367046420?w=400&h=600&fit=crop&sat=20&con=10", // Vibrant gaming setup
    "https://images.unsplash.com/photo-1493711662062-fa541adb3fc8?w=400&h=600&fit=crop&sat=20&con=15", // Colorful friends gaming
    "https://images.unsplash.com/photo-1606092195730-5d7b9af1efc5?w=400&h=600&fit=crop&sat=15&con=10", // Bright playing cards
    "https://images.unsplash.com/photo-1566694271453-390536dd1f0d?w=400&h=600&fit=crop&sat=20&con=10", // Colorful chess setup
  ],
  [ActivityCategory.Outdoor]: [
    "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=600&fit=crop&sat=25&con=15", // Bright sunny hiking
    "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=400&h=600&fit=crop&sat=25&con=10", // Vibrant beach volleyball
    "https://images.unsplash.com/photo-1506197603052-3cc9c3a201bd?w=400&h=600&fit=crop&sat=20&con=15", // Colorful camping scene
    "https://images.unsplash.com/photo-1464822759844-d150065c142a?w=400&h=600&fit=crop&sat=20&con=10", // Bright mountain landscape
    "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=600&fit=crop&sat=15&con=10", // Sunlit forest trail
  ],
  [ActivityCategory.Drinks]: [
    "https://images.unsplash.com/photo-1551218808-94e220e084d2?w=400&h=600&fit=crop&sat=25&con=15", // Vibrant colorful cocktails
    "https://images.unsplash.com/photo-1544145945-f90425340c7e?w=400&h=600&fit=crop&sat=20&con=10", // Bright bar cocktail scene
    "https://images.unsplash.com/photo-1551538827-9c037cb4f32a?w=400&h=600&fit=crop&sat=20&con=15", // Colorful bright coffee shop
    "https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=400&h=600&fit=crop&sat=20&con=10", // Bright craft beer
    "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=400&h=600&fit=crop&sat=15&con=10", // Warm wine tasting
  ],
  [ActivityCategory.Fitness]: [
    "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=400&h=600&fit=crop&sat=25&con=15", // Bright outdoor running
    "https://images.unsplash.com/photo-1549060279-7e168fcee0c2?w=400&h=600&fit=crop&sat=20&con=10", // Colorful yoga class
    "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=600&fit=crop&sat=20&con=15", // Vibrant gym equipment
    "https://images.unsplash.com/photo-1538805060514-97d9cc17730c?w=400&h=600&fit=crop&sat=20&con=10", // Bright personal training
    "https://images.unsplash.com/photo-1485727749690-d091e8284ef3?w=400&h=600&fit=crop&sat=15&con=10", // Colorful weight training
  ],
  [ActivityCategory.General]: [
    "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=400&h=600&fit=crop&sat=25&con=15", // Bright colorful community gathering
    "https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=400&h=600&fit=crop&sat=20&con=10", // Vibrant friends meetup
    "https://images.unsplash.com/photo-1543269664-647b59cd2b84?w=400&h=600&fit=crop&sat=20&con=15", // Colorful networking event
    "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=600&fit=crop&sat=20&con=10", // Bright group discussion
    "https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?w=400&h=600&fit=crop&sat=15&con=10", // Colorful workshop scene
  ],
};

interface UnsplashImage {
  id: string;
  urls: {
    small: string;
    regular: string;
    full: string;
  };
  alt_description: string;
  description: string;
  tags: Array<{ title: string }>;
}

interface UnsplashResponse {
  results: UnsplashImage[];
  total: number;
  total_pages: number;
}

/**
 * Generate search terms based on activity details - prioritizing AI-generated keywords, then specific subjects from title and description
 *
 * @deprecated This function is no longer used in the main flow.
 * Use getImageStrategy() instead which provides optimized queries directly.
 * Kept for backward compatibility.
 */
async function generateSearchTerms(
  category: ActivityCategory,
  title?: string,
  description?: string,
): Promise<string[]> {
  const baseKeywords = CATEGORY_KEYWORDS[category];

  // Try to get AI-generated keywords first (most relevant)
  let aiKeywords: string[] = [];
  if (title || description) {
    try {
      aiKeywords = await generateAIKeywords(category, title, description);
    } catch (error) {
      console.warn("Failed to get AI keywords, using fallback:", error);
    }
  }

  // If we got AI keywords, prioritize them
  if (aiKeywords.length > 0) {
    return [
      ...aiKeywords,
      ...baseKeywords.slice(0, 2), // Add some base keywords as backup
    ];
  }

  // Comprehensive activity keyword mapping for better image relevance
  const activityKeywords: Record<string, string[]> = {
    // Food & Dining
    brunch: ["brunch", "breakfast", "morning meal", "avocado toast"],
    coffee: ["coffee", "cafe", "espresso", "latte", "cappuccino", "barista"],
    pizza: ["pizza", "pizzeria", "italian food"],
    sushi: ["sushi", "japanese food", "sashimi", "ramen"],
    burger: ["burger", "hamburger", "fast food"],
    dessert: ["dessert", "cake", "pastry", "bakery", "sweet"],
    bbq: ["bbq", "barbecue", "grilled", "barbeque"],
    seafood: ["seafood", "fish", "lobster", "oyster"],
    vegetarian: ["vegetarian", "vegan", "plant-based"],
    "fine dining": ["fine dining", "gourmet", "michelin"],
    "street food": ["street food", "food truck", "market"],

    // Drinks
    wine: ["wine", "vineyard", "wine tasting", "cellar", "sommelier"],
    beer: ["beer", "brewery", "craft beer", "pub", "ale"],
    cocktail: ["cocktail", "mixology", "bar", "drinks", "bartender"],
    tea: ["tea", "tea ceremony", "teahouse"],
    smoothie: ["smoothie", "juice", "healthy drink"],

    // Outdoor Activities
    hiking: ["hiking", "trail", "mountain", "trek", "backpacking"],
    beach: ["beach", "ocean", "seaside", "sand", "coastal"],
    camping: ["camping", "tent", "campfire", "outdoors"],
    cycling: ["cycling", "biking", "bicycle", "bike ride"],
    kayaking: ["kayaking", "paddling", "water sports"],
    picnic: ["picnic", "outdoor meal", "park"],
    "rock climbing": ["rock climbing", "bouldering", "climbing"],
    skiing: ["skiing", "snow", "winter sports"],
    surfing: ["surfing", "waves", "beach"],

    // Fitness & Sports
    yoga: ["yoga", "meditation", "mindfulness", "zen"],
    gym: ["gym", "fitness", "workout", "training", "exercise"],
    running: ["running", "jogging", "runner", "marathon"],
    swimming: ["swimming", "pool", "swim"],
    crossfit: ["crossfit", "functional training"],
    pilates: ["pilates", "mat workout"],
    dance: ["dance", "dancing", "choreography", "ballet"],
    boxing: ["boxing", "martial arts", "kickboxing"],
    basketball: ["basketball", "hoops", "court"],
    tennis: ["tennis", "racquet", "court"],
    soccer: ["soccer", "football", "pitch"],

    // Entertainment & Culture
    movie: ["movie", "film", "cinema", "theater", "screening"],
    concert: ["concert", "live music", "band", "performance"],
    museum: ["museum", "art gallery", "exhibition", "artifacts"],
    theater: ["theater", "play", "performance", "stage"],
    comedy: ["comedy", "stand up", "comedian", "laughs"],
    opera: ["opera", "classical music", "performance"],
    jazz: ["jazz", "live jazz", "jazz club"],

    // Gaming & Entertainment
    "board games": ["board games", "tabletop", "game night"],
    "video games": ["video games", "gaming", "esports", "console"],
    arcade: ["arcade", "retro games", "game center"],
    karaoke: ["karaoke", "singing", "karaoke bar"],
    bowling: ["bowling", "bowling alley", "pins"],
    "escape room": ["escape room", "puzzle", "mystery"],
    trivia: ["trivia", "quiz", "pub quiz"],

    // Social & Learning
    "cooking class": ["cooking class", "chef", "kitchen", "culinary"],
    "art class": ["art class", "painting", "drawing", "creative"],
    workshop: ["workshop", "learning", "hands-on"],
    "book club": ["book club", "reading", "books", "literature"],
    "language exchange": ["language exchange", "learning", "conversation"],
    networking: ["networking", "professional", "business"],
    volunteering: ["volunteering", "charity", "community service"],

    // Relaxation & Wellness
    spa: ["spa", "massage", "wellness", "relaxation"],
    meditation: ["meditation", "mindfulness", "peaceful"],
    sauna: ["sauna", "steam", "wellness"],

    // Shopping & Exploration
    shopping: ["shopping", "mall", "boutique", "retail"],
    market: ["market", "bazaar", "vendors", "local market"],
    photography: ["photography", "photos", "camera", "photoshoot"],
    sightseeing: ["sightseeing", "tourist", "landmarks", "exploring"],
  };

  // Extract specific keywords from title and description
  const extractedKeywords: string[] = [];
  const combinedText = `${title || ""} ${description || ""}`.toLowerCase();

  // Find all matching activity keywords, prioritizing longer matches
  const matches: Array<{ key: string; priority: number }> = [];
  for (const [key, keywords] of Object.entries(activityKeywords)) {
    for (const keyword of keywords) {
      if (combinedText.includes(keyword)) {
        // Priority based on keyword length and position (title > description)
        const inTitle = (title || "").toLowerCase().includes(keyword);
        const priority = keyword.length + (inTitle ? 100 : 0);
        matches.push({ key, priority });
        break; // Only count each key once
      }
    }
  }

  // Sort by priority and take top matches
  matches.sort((a, b) => b.priority - a.priority);
  const topMatches = matches.slice(0, 3).map((m) => m.key);

  // Build search terms with extracted keywords taking precedence
  if (topMatches.length > 0) {
    return [
      // Most specific: primary extracted keyword
      topMatches[0],
      // Add context with category for better results
      `${topMatches[0]} ${baseKeywords[0].split(" ")[0]}`,
      // Secondary extracted keywords
      ...topMatches.slice(1).map((k) => k),
      // Fall back to base category keywords
      ...baseKeywords,
    ];
  }

  return baseKeywords;
}

/**
 * Search for images using Unsplash API
 */
async function searchUnsplashImages(
  query: string,
  count: number = 3,
  orientation: "portrait" | "landscape" | "squarish" | "" = "portrait",
): Promise<UnsplashImage[]> {
  try {
    const url = new URL(UNSPLASH_API_URL);
    // Use simpler query without extra filters for better results
    url.searchParams.set("query", encodeURIComponent(query));
    url.searchParams.set("per_page", Math.min(count, 30).toString()); // Unsplash max is 30
    // Try portrait first, but don't make it mandatory
    if (orientation) {
      url.searchParams.set("orientation", orientation);
    }
    url.searchParams.set("content_filter", "high");
    url.searchParams.set("order_by", "relevant"); // Prioritize relevance first


    // Check if API key is available
    const { UNSPLASH_ACCESS_KEY } = await getApiKeys();
    if (!UNSPLASH_ACCESS_KEY) {
      throw new Error("Unsplash API key not configured");
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    try {
      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Client-ID ${UNSPLASH_ACCESS_KEY}`,
          "Accept-Version": "v1",
        },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!response.ok) {
        let errorText = "";
        let errorDetails = "";
        try {
          errorText = await response.text();
          try {
            const errorJson = JSON.parse(errorText);
            errorDetails =
              errorJson.errors?.join(", ") || errorJson.error || errorText;
          } catch {
            errorDetails = errorText;
          }
        } catch (e) {
          errorDetails = "Unable to read error response";
        }
        crashlytics.logError(
          `Unsplash API Error: Status ${response.status}, Query: "${query}", Details: ${errorDetails}, URL: ${url.toString().replace(UNSPLASH_ACCESS_KEY!, "REDACTED")}`,
          "imageService.ts",
          new Error(`Unsplash API error`),
        );
        throw new Error(
          `Unsplash API error: ${response.status} - ${errorDetails}`,
        );
      }

      const data: UnsplashResponse = await response.json();
      if (data.total === 0) {
        console.warn(
          `[Unsplash API] Zero results - Query too specific? Try broader terms. Total available: ${data.total}`,
        );
      }
      return data.results || [];
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === "AbortError") {
        throw new Error("Unsplash API request timed out");
      }
      throw error;
    }
  } catch (error) {
    crashlytics.logError(
      `Error in searchUnsplashImages: ${JSON.stringify(error?.message)}`,
      "imageService.ts",
      error,
    );
    throw error;
  }
}

// Request deduplication cache to prevent duplicate API calls
const requestCache = new Map<
  string,
  { promise: Promise<string>; timestamp: number }
>();
const REQUEST_CACHE_TTL = 5000; // Cache requests for 5 seconds

/**
 * Get a relevant image URL for an activity
 */
export async function getActivityImageUrl(
  category: ActivityCategory,
  title?: string,
  description?: string,
  location?: string,
): Promise<string> {
  callCount++;

  // Create cache key from request parameters
  const cacheKey = `${category}-${title || ""}-${description || ""}-${location || ""}`;

  // Check if we have a recent request for the same parameters
  const cached = requestCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < REQUEST_CACHE_TTL) {
    return cached.promise;
  }


  // Create new request promise and cache it
  const promise = getImageInternal(category, title, description, location).then(
    (result) => {
      return result;
    },
  );

  requestCache.set(cacheKey, { promise, timestamp: Date.now() });

  // Clean up old cache entries periodically
  setTimeout(() => {
    const entry = requestCache.get(cacheKey);
    if (entry && Date.now() - entry.timestamp >= REQUEST_CACHE_TTL) {
      requestCache.delete(cacheKey);
    }
  }, REQUEST_CACHE_TTL);

  return promise;
}

/**
 * Internal function that does the actual image fetching
 */
async function getImageInternal(
  category: ActivityCategory,
  title?: string,
  description?: string,
  location?: string,
): Promise<string> {

  // Normalize the category to ensure it's valid
  const normalizedCategory = normalizeCategory(category);
  if (normalizedCategory !== category) {
  }

  // Use AI to determine the best image source and search strategy
  const strategy = await getImageStrategy(
    normalizedCategory,
    title,
    description,
    location,
  );

  // Try the AI-recommended source first
  if (strategy.source === "tmdb") {
    try {
      // Use AI-provided query (cleaned movie title)
      const moviePoster = await searchMoviePosterDirect(strategy.query);
      if (moviePoster) {
        return moviePoster;
      }
    } catch (error) {
      console.warn("Failed to get TMDB poster, trying fallback:", error);
    }
  } else if (strategy.source === "google_places") {
    try {
      // Use AI-provided query (venue name + location)
      const venuePhoto = await searchVenuePhotoDirect(strategy.query);
      if (venuePhoto) {
        return venuePhoto;
      }
    } catch (error) {
      console.warn(
        "Failed to get Google Places photo, trying fallback:",
        error,
      );
    }
  }

  // If AI recommends Unsplash or if other sources failed, use Unsplash with AI query

  try {
    // Use AI-provided query as primary search term
    const searchTerms = [
      strategy.query, // AI-optimized query
      CATEGORY_KEYWORDS[normalizedCategory][0], // Category fallback
      CATEGORY_KEYWORDS[normalizedCategory][1] ||
        CATEGORY_KEYWORDS[normalizedCategory][0], // Broader fallback
    ].filter((query) => query && query.length > 0);


    // Try multiple search queries in order of specificity
    const searchQueries = [
      // Most specific: use generated search terms (includes activity-specific keywords)
      searchTerms[0],
      // Medium specific: fallback to primary category keyword
      CATEGORY_KEYWORDS[normalizedCategory][0],
      // Least specific: broader category search
      CATEGORY_KEYWORDS[normalizedCategory][1] ||
        CATEGORY_KEYWORDS[normalizedCategory][0],
    ].filter((query) => query && query.length > 0);

    // Try each search query until we get results
    for (const query of searchQueries) {
      try {

        // First try with portrait orientation
        let images = await searchUnsplashImages(query, 5, "portrait");

        // If no portrait results, try any orientation
        if (images.length === 0) {
          images = await searchUnsplashImages(query, 5, "");
        }

        if (images.length > 0) {
          // Return a random image from the results with subtle brightness enhancement
          const selectedImage =
            images[Math.floor(Math.random() * images.length)];
          const finalUrl =
            selectedImage.urls.regular +
            "?w=400&h=600&fit=crop&sat=15&bri=5&con=5&auto=format";
          return finalUrl;
        } else {
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        crashlytics.logError(
          `Unsplash API failed for "${query}": ${errorMsg}${error instanceof Error && error.stack ? ", Stack: " + error.stack : ""}`,
          "imageService.ts",
          error,
        );
        // Don't break the loop, try the next query or fallback
        continue;
      }
    }

    // If all Unsplash searches fail, fall back to curated images

    // Use curated images for variety
    const curatedImages = CURATED_IMAGES[normalizedCategory];
    if (curatedImages && curatedImages.length > 0) {
      const selectedCurated =
        curatedImages[Math.floor(Math.random() * curatedImages.length)];
      return selectedCurated;
    }

    // Try Games category as backup
    const gamesCurated = CURATED_IMAGES[ActivityCategory.Games];
    if (gamesCurated && gamesCurated.length > 0) {
      return gamesCurated[Math.floor(Math.random() * gamesCurated.length)];
    }

    // Try FALLBACK_IMAGES
    const fallbackImage =
      FALLBACK_IMAGES[normalizedCategory] ||
      FALLBACK_IMAGES[ActivityCategory.General];
    if (fallbackImage) {
      return fallbackImage;
    }

    // Absolute last resort
    return fallBackImage;
  } catch (error) {
    crashlytics.logError(
      `Error in getActivityImageOptions: ${JSON.stringify(error?.message)}`,
      "imageService.ts",
      error,
    );
    // Final fallback with random selection from curated images
    const curatedImages = CURATED_IMAGES[normalizedCategory];
    if (curatedImages && curatedImages.length > 0) {
      const fallbackUrl =
        curatedImages[Math.floor(Math.random() * curatedImages.length)];
      return fallbackUrl;
    }

    // Try Games category curated images as backup
    const gamesCurated = CURATED_IMAGES[ActivityCategory.Games];
    if (gamesCurated && gamesCurated.length > 0) {
      const fallbackUrl =
        gamesCurated[Math.floor(Math.random() * gamesCurated.length)];
      return fallbackUrl;
    }

    // Try FALLBACK_IMAGES as another backup
    const fallbackImage =
      FALLBACK_IMAGES[normalizedCategory] ||
      FALLBACK_IMAGES[ActivityCategory.General];
    if (fallbackImage) {
      return fallbackImage;
    }

    // Absolute last resort - return the imported fallback image
    return fallBackImage;
  }
}

/**
 * Get multiple relevant images for an activity (useful for photo selection)
 */
export async function getActivityImageOptions(
  category: ActivityCategory,
  title?: string,
  description?: string,
  location?: string,
  count: number = 6,
): Promise<string[]> {
  // Normalize the category to ensure it's valid
  const normalizedCategory = normalizeCategory(category);

  // Helper to get unique curated images efficiently
  const getUniqueCuratedImages = (
    neededCount: number,
    existingUrls: Set<string> = new Set(),
  ): string[] => {
    const result: string[] = [];
    const used = new Set(existingUrls);

    // Add from current category (ensuring fallback appears at most once)
    const curatedImages = CURATED_IMAGES[normalizedCategory] || [];
    for (const url of curatedImages) {
      if (result.length >= neededCount) break;
      if (!used.has(url)) {
        result.push(url);
        used.add(url);
      }
    }

    // If still need more, add from other categories (ensuring fallback appears at most once)
    if (result.length < neededCount) {
      const allOtherImages = Object.entries(CURATED_IMAGES)
        .filter(([cat]) => cat !== normalizedCategory)
        .flatMap(([_, urls]) => urls);

      for (const url of allOtherImages) {
        if (result.length >= neededCount) break;
        if (!used.has(url)) {
          result.push(url);
          used.add(url);
        }
      }
    }

    return result;
  };

  try {
    // Use AI strategy to get optimized query (same as main image fetch)
    const strategy = await getImageStrategy(
      normalizedCategory,
      title,
      description,
      location,
    );

    // Try the AI-recommended source first (same order as getActivityImageUrl)
    if (strategy.source === "tmdb") {
      const moviePoster = await searchMoviePosterDirect(strategy.query);
      if (moviePoster) {
        // For movie posters, if count is 1, return single poster
        // Otherwise, pad with curated images (no duplicates)
        if (count === 1) {
          return [moviePoster];
        }
        const result = [moviePoster];
        const additionalImages = getUniqueCuratedImages(
          count - 1,
          new Set([moviePoster]),
        );
        return [...result, ...additionalImages].slice(0, count);
      }
    } else if (strategy.source === "google_places") {
      const venuePhoto = await searchVenuePhotoDirect(strategy.query);
      if (venuePhoto) {
        // For venue photos, if count is 1, return single photo
        // Otherwise, pad with curated images (no duplicates)
        if (count === 1) {
          return [venuePhoto];
        }
        const result = [venuePhoto];
        const additionalImages = getUniqueCuratedImages(
          count - 1,
          new Set([venuePhoto]),
        );
        return [...result, ...additionalImages].slice(0, count);
      }
    }

    // For Unsplash or if other sources failed, search with AI query
    const query = strategy.query;

    try {
      const images = await searchUnsplashImages(query, count);

      // Deduplicate Unsplash results efficiently
      const uniqueUrls = new Set<string>();
      const uniqueImageUrls: string[] = [];

      for (const image of images) {
        const url =
          image.urls.regular +
          "?w=400&h=600&fit=crop&sat=15&bri=5&con=5&auto=format";
        if (!uniqueUrls.has(url)) {
          uniqueImageUrls.push(url);
          uniqueUrls.add(url);
        }
      }

      // If we need more images, pad with unique curated images
      if (uniqueImageUrls.length < count) {
        const additionalImages = getUniqueCuratedImages(
          count - uniqueImageUrls.length,
          uniqueUrls,
        );
        uniqueImageUrls.push(...additionalImages);
      }

      return uniqueImageUrls.slice(0, count);
    } catch (unsplashError) {
      console.warn(
        "Unsplash search failed for image options, using curated images:",
        unsplashError,
      );
      // Return unique curated images only
      return getUniqueCuratedImages(count);
    }
  } catch (error) {
    crashlytics.logError(
      `Error in getActivityImageOptions: ${JSON.stringify(error?.message)}`,
      "imageService.ts",
      error,
    );
    // Return unique curated images as fallback
    return getUniqueCuratedImages(count);
  }
}

/**
 * Preload images to improve UX
 */
export async function preloadImage(
  url: string,
  giveFallback: boolean = true,
): Promise<string | null> {
  const loadImage = (src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => reject();
      img.src = src;
    });
  };

  try {
    await loadImage(url);
    return url; // Success!
  } catch {
    if (url !== "non-existent-url") {
      console.warn(`Failed to load: ${url}, preloading fallback...`);
    }
    if (!giveFallback) {
      return null; // Return empty if no fallback desired
    }
    try {
      await loadImage(fallBackImage);
      return fallBackImage; // Use fallback
    } catch {
      return fallBackImage; // Last resort
    }
  }
}

/**
 * Test function to check if Unsplash API is working
 */
export async function testUnsplashConnection(): Promise<boolean> {
  try {
    const images = await searchUnsplashImages("coffee", 1);
    return images.length > 0;
  } catch (error) {
    crashlytics.logError(
      `Error in testUnsplashConnection: ${JSON.stringify(error?.message)}`,
      "imageService.ts",
      error,
    );
    return false;
  }
}
